import { forwardRef, useId, type SelectHTMLAttributes, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Re-authored from ../design-system/components/forms/Select.jsx.
 * Styled native <select> with custom ChevronDown chevron. Native for a11y + mobile.
 * Chrome matches Input: sunken bg, gold focus ring.
 */

export type SelectOption = string | { value: string; label: ReactNode };

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, placeholder, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const selectId = id ?? autoId;

  return (
    <div className={cn('flex flex-col gap-2 font-sans', className)}>
      {label && (
        <label
          htmlFor={selectId}
          className="text-sm font-semibold text-[var(--text-heading)]"
        >
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        <select
          id={selectId}
          ref={ref}
          className={cn(
            // Layout + typography
            'w-full appearance-none font-sans text-base text-foreground',
            // Chrome: sunken bg, border, radius, height, padding (leave pr-10 for chevron)
            'bg-input border border-border rounded-[var(--radius-control)] h-10 pl-4 pr-10',
            // Transition
            'transition',
            // Hover (only when enabled and not focused)
            '[&:enabled:hover:not(:focus)]:border-[var(--ollio-slate)]',
            // Focus: card bg + gold border + focus-glow ring
            'focus:outline-none focus:bg-card focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]',
            // Disabled
            'disabled:opacity-55 disabled:cursor-not-allowed',
            // Cursor
            'cursor-pointer',
          )}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => {
            const opt = typeof o === 'string' ? { value: o, label: o } : o;
            return (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            );
          })}
        </select>

        {/* Absolutely-positioned chevron — pointer-events-none so it doesn't interfere */}
        <span className="pointer-events-none absolute right-3 flex text-muted [&_svg]:h-[18px] [&_svg]:w-[18px]">
          <ChevronDown aria-hidden="true" />
        </span>
      </div>
    </div>
  );
});
